import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Mail, Navigation } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.echt.gold';

const Standort = () => {
  return (
    <>
      <Helmet>
        <title>Standort & Anfahrt - Echt Gold in Dortmund</title>
        <meta name="description" content="Besuchen Sie uns in Dortmund. Anfahrt, Parken, Öffnungszeiten - alle Informationen zu unserem Standort." />
        <link rel="canonical" href={`${BASE_URL}/standort`} />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Echt Gold",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Neben dem Brand 10",
            "addressLocality": "Dortmund",
            "postalCode": "44135",
            "addressCountry": "DE"
          },
          "telephone": "+49-231-39085116",
          "email": "Info@echtgold.de",
          "openingHours": [
            "Mo-Fr 08:00-18:00",
            "Sa 09:00-17:00"
          ]
        })}
      </script>

      <div className="bg-black/90 py-8 border-b border-gold/20">
        <div className="container mx-auto px-4">
          <div className="text-sm text-gray-400 mb-2">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-gold">Standort</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold gold-text">Unser Standort</h1>
        </div>
      </div>

      <section className="section section-dark">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6 gold-text">Besuchen Sie uns in Dortmund</h2>
              <p className="text-gray-300 mb-8 text-lg">
                Unser Standort befindet sich in zentraler Lage in Dortmund. Wir freuen uns auf Ihren Besuch!
              </p>

              <div className="space-y-6">
                <div className="card">
                  <div className="flex gap-4 items-start">
                    <MapPin className="text-gold flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold mb-2">Adresse</h3>
                      <p className="text-gray-300">
                        Echt Gold<br />
                        Neben dem Brand 10<br />
                        44135 Dortmund
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex gap-4 items-start">
                    <Clock className="text-gold flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold mb-2">Öffnungszeiten</h3>
                      <p className="text-gray-300">
                        Montag - Freitag: 08:00 - 18:00 Uhr<br />
                        Samstag: 09:00 - 17:00 Uhr<br />
                        Sonntag: Geschlossen
                      </p>
                      <p className="text-sm text-gold mt-2">
                        Termine nach Vereinbarung auch außerhalb der Öffnungszeiten möglich
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex gap-4 items-start">
                    <Navigation className="text-gold flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold mb-2">Anfahrt & Parken</h3>
                      <p className="text-gray-300">
                        <strong>Mit dem Auto:</strong> Parkplätze in der Nähe vorhanden<br />
                        <strong>Mit ÖPNV:</strong> Bushaltestelle in Fußnähe<br />
                        <strong>Hauptbahnhof:</strong> Dortmund Hbf (ca. 3 km entfernt)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex gap-4 items-start">
                    <Phone className="text-gold flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold mb-2">Kontakt</h3>
                      <p className="text-gray-300">
                        Telefon: <a href="tel:023139085116" className="text-gold hover:underline">0231 39085116</a><br />
                        E-Mail: <a href="mailto:Info@echtgold.de" className="text-gold hover:underline">Info@echtgold.de</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="card h-full">
                <h3 className="text-2xl font-bold mb-4 gold-text">Karte</h3>
                <div className="bg-gray-800 rounded-lg overflow-hidden" style={{height: '400px'}}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2485.5!2d7.4653!3d51.5136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDMwJzQ5LjAiTiA3wrAyNyc1NS4xIkU!5e0!3m2!1sde!2sde!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{border: 0}}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Echt Gold Standort"
                  />
                </div>
                <p className="text-sm text-gray-400 mt-4">
                  Neben dem Brand 10, 44135 Dortmund
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <div className="card text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 gold-text">Termin vereinbaren</h2>
            <p className="text-gray-300 mb-8">
              Für einen reibungslosen Ablauf empfehlen wir eine Terminvereinbarung
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary">Jetzt Termin vereinbaren</Link>
              <a href="tel:023139085116" className="btn-secondary">0231 39085116</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Standort;